import { useState, useEffect, useMemo, useRef, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { ModalContext } from 'components/Modals/ModalContext'
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import { GetWorkoutTemplateNameFromKey } from 'functions/textUtils';
import { CalendarViewsConfig, GetDayCellContent, GetDayCellClassNames, GetDayHeaderContent } from 'functions/calendarUtils';
import { setActiveWeekWeekDaySchedule, addTrainingPeaksWorkoutCalendarEventItems, addOverrideWeekDaySchedule, removeOverrideWeekDaySchedule, clearProgramTableOverrides } from 'redux/slices/athleteProfile';
import { WhichWeightAPI } from 'WhichWeightAPI';
import { ff, flags } from 'functions/roleBasedAccessUtils'
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from 'moment';
import loadingSpinner from 'media/loading.svg'

import ContentFrame from 'components/Frame/ContentFrame'
import FullCalendar from "@fullcalendar/react";
import CalendarEventItemRouter from 'components/Athlete/v2/AthleteCalendar/AthleteCalendarEventItems/CalendarEventItemRouter'
import PresentWeekHighlighter from 'components/Athlete/v2/AthleteCalendar/PresentWeekHighlighter'
import AthleteCalendarProgramWeeks from 'components/Athlete/v2/AthleteCalendar/AthleteCalendarProgramWeeks'
import OutlineButton from 'components/Buttons/OutlineButton';
import BrandColorButton from 'components/Buttons/BrandColorButton'

export default function AthleteCalendar({ 
    calendarRef,
    kinesisEvents,
    NavigateCalendarToPreviousWeek = () => {},
    NavigateCalendarToNextWeek = () => {}
}) {
    const dispatch = useDispatch()

    const { modalData } = useContext(ModalContext)

    const coachKinesisUserId = useSelector(state => state.user.id)

    const profileType = useSelector(state => state.athleteProfile.profileType)
    const groupId = useSelector(state => state.athleteProfile.groupId)
    const athleteId = useSelector(state => state.athleteProfile.userId)
    const trainingPeaksUserId = useSelector(state => state.athleteProfile.trainingPeaksUserId)
    const defaultWeekDaySchedule = useSelector(state => state.athleteProfile.defaultWeekDaySchedule)
    const overrideWeekDaySchedules = useSelector(state => state.athleteProfile.overrideWeekDaySchedules)
    const trainingChartPresentDate = useSelector(state => state.athleteProfile.trainingChartPresentDate)
    const trainingChartPresentWeekOfTheYearKey = useSelector(state => state.athleteProfile.trainingChartPresentWeekOfTheYearKey)
    const trainingChartPastData = useSelector(state => state.athleteProfile.pastData)
    const cachedOverrides = useSelector((state) => state.athleteProfile.programTableOverrides);

    const trainingChartActiveData = useSelector(state => state.athleteProfile.activeData)
    const latestActiveDataRef = useRef(trainingChartActiveData) // Use a ref here to make sure redux slowness does not effect drag/drop functionality

    const trainingChartLatestProgramSnapshot = useSelector(state => state.athleteProfile.latestProgramSnapshot)
    const trainingChartSelectedWeekOfTheYearKey = useSelector(state => state.athleteProfile.trainingChartSelectedWeekOfTheYearKey)

    const trainingPeaksWorkoutCalendarEventItems = useSelector(state => state.athleteProfile.trainingPeaksWorkoutCalendarEventItems)

    const [kinesisEventCalendarItems, setKinesisEventCalendarItems] = useState([])
    const [pastKinesisWorkoutCalendarEventItems, setPastKinesisWorkoutCalendarEventItems] = useState([])
    const [activeKinesisWorkoutCalendarEventItems, setActiveKinesisWorkoutCalendarEventItems] = useState([])
    const [futureKinesisWorkoutCalendarEventItems, setFutureKinesisWorkoutCalendarEventItems] = useState([])
    
    // Drag/drop sync variables for active week
    const latestWeekDayScheduleSyncDataRef = useRef(null) // { jsonBody, rollBackWeekDaySchedule, callId }
    const isProcessingWeekDayScheduleSyncDataRef = useRef(false)
    const weekDayScheduleSyncAPICounterRef = useRef(0)

    // Drag/drop sync variables for future week overrides
    const latestOverrideWeekDayScheduleSyncDataRef = useRef(null) // { jsonBody, rollBackWeekOfTheYearKey, callId }
    const isProcessingOverrideWeekDayScheduleSyncDataRef = useRef(false)
    const overrideWeekDayScheduleSyncAPICounterRef = useRef(0)

    const [syncingMessage, setSyncingMessage] = useState('')
    const [savingOverrides, setSavingOverrides] = useState(false)

    // Present week overlay positioning
    const calendarContainerRef = useRef(null)

    const canEditKinesisProfileWeekDaySchedules = ff(flags.canEditKinesisProfileWeekDaySchedules, { profileType: profileType })

    // TrainingPeaks Functions

    const GetTrainingPeaksWorkouts = async (startDate, endDate) => {

        // console.log('CALLING TRAINING PEAKS WORKOUTS API')

        setSyncingMessage('Syncing TrainingPeaks Calendar...')

        try {
            const jsonBody = {
                userId: coachKinesisUserId, // Needs to be named userId for authorizer
                athleteKinesisUserId: athleteId,
                startDate: startDate,
                endDate: endDate
            }

            // console.log(jsonBody)

            const result = await WhichWeightAPI('CORE', 'GET', '/training_peaks_athlete_workouts', jsonBody);

            // console.log(result.data.data)

            setSyncingMessage('')

            // Push Kinesis Workouts to TrainingPeaks. Does nothing if already pushed

            const pushJsonBody = {
                userId: athleteId,
                trainingPeaksUserId: trainingPeaksUserId,
                weekOfTheYearKey: trainingChartPresentWeekOfTheYearKey
            }

            // console.log(pushJsonBody)

            WhichWeightAPI('CORE', 'POST', '/training_peaks_kinesis_workout_push', pushJsonBody)

            if (result.data.status === 'failure') return []
            if (result?.data?.data === undefined) return []
            if (result?.data?.data === null) return []
            if (result?.data?.data?.length === 0) return []

            return result?.data?.data
        }
        catch (error) {
            setSyncingMessage('')
            console.error('Error getting training peaks workouts!!', error)
            return []
        }
    }

    // Calendar Item Processing Functions

    const ProcessKinesisEventsIntoCalendarItems = () => {
        const new_events = []

        for (let kinesisEvent of kinesisEvents) {
            new_events.push({
                title: kinesisEvent.name,
                start: kinesisEvent.eventDate,
                extendedProps: {
                    calendarEventType: 'kinesis-event',
                    kinesisEventData: kinesisEvent
                }
            })
        }

        setKinesisEventCalendarItems(new_events)
    }

    const ProcessTrainingChartDataIntoPastKinesisWorkoutCalendarEventItems = () => {

        var new_events = []

        // Adding Past Calendar Data

        for (let pastDataWeek of trainingChartPastData) {
            
            // Get the start date of the current week of the year key

            var weekOfTheYearKey = new WeekOfTheYearKey(pastDataWeek.weekOfTheYearKey)
            var weekOfTheYearKeyStartDate = weekOfTheYearKey.toDate()

            // Process week day schedule to remove strength workouts if the week is skipped

            var processedWeekDaySchedule = JSON.parse(JSON.stringify(pastDataWeek.weekDaySchedule))
            if (pastDataWeek.weekCompletionStatusKey === 'skipped') processedWeekDaySchedule = RemoveStrengthWorkoutsFromWeekDaySchedule(processedWeekDaySchedule)

            // Add calendar items for the week

            for (let weekDayIndex of Object.keys(processedWeekDaySchedule)) {

                var workoutTemplateKey = processedWeekDaySchedule[weekDayIndex]

                // Do not display anything for rest days on the calendar
                if (workoutTemplateKey === 'rest') continue

                new_events.push({
                    title: GetWorkoutTemplateNameFromKey(workoutTemplateKey),
                    start: weekOfTheYearKeyStartDate.clone().add(weekDayIndex, 'days').format('YYYY-MM-DD'),
                    extendedProps: {
                        calendarEventType: 'kinesis-workout',
                        calendarDataKey: pastDataWeek.calendarDataKey,
                        weekOfTheYearKey: pastDataWeek.weekOfTheYearKey,
                        workoutTemplateKey: workoutTemplateKey,
                        completionData: pastDataWeek.completionData[workoutTemplateKey]
                    }
                })

            }

        }

        setPastKinesisWorkoutCalendarEventItems(new_events)

    }

    const ProcessTrainingChartDataIntoActiveKinesisWorkoutCalendarEventItems = () => {

        // console.log(trainingChartActiveData)

        var new_events = []

        const presentDate = moment(trainingChartPresentDate)
        var weekOfTheYearKey = new WeekOfTheYearKey(trainingChartActiveData.weekOfTheYearKey)
        var weekOfTheYearKeyStartDate = weekOfTheYearKey.toDate()

        // Process week day schedule to remove strength workouts if the week is skipped

        var processedWeekDaySchedule = JSON.parse(JSON.stringify(trainingChartActiveData.weekDaySchedule))
        if (trainingChartActiveData.weekCompletionStatusKey === 'skipped') processedWeekDaySchedule = RemoveStrengthWorkoutsFromWeekDaySchedule(processedWeekDaySchedule)

        const draggableDateRangeConstraint = GetDraggableDateRangeConstraintForEvent(trainingChartActiveData.weekOfTheYearKey);

        // Add calendar items for the week

        for (let weekDayIndex of Object.keys(processedWeekDaySchedule)) {

            var workoutTemplateKey = processedWeekDaySchedule[weekDayIndex]

            // Do not display anything for rest days on the calendar
            if (workoutTemplateKey === 'rest') continue

            var completionData = trainingChartActiveData.completionData[workoutTemplateKey]
            var scheduledWorkoutDate = weekOfTheYearKeyStartDate.clone().add(weekDayIndex, 'days')
            var isEventDraggable = completionData.completed ? false : true

            new_events.push({
                title: GetWorkoutTemplateNameFromKey(workoutTemplateKey),
                start: scheduledWorkoutDate.format('YYYY-MM-DD'),
                editable: canEditKinesisProfileWeekDaySchedules && isEventDraggable,
                constraint: draggableDateRangeConstraint,
                extendedProps: {
                    athleteId: athleteId,
                    calendarEventType: 'kinesis-workout',
                    calendarDataKey: trainingChartActiveData.calendarDataKey,
                    weekOfTheYearKey: trainingChartActiveData.weekOfTheYearKey,
                    taperData: trainingChartActiveData.taperData,
                    programPhaseKey: trainingChartActiveData.programPhaseKey,
                    programPhaseWeekIndex: trainingChartActiveData.programPhaseWeekIndex,
                    workoutTemplateKey: workoutTemplateKey,
                    completionData: completionData,
                    isInThePast: scheduledWorkoutDate.isBefore(presentDate)
                }
            })

        }

        setActiveKinesisWorkoutCalendarEventItems(new_events)

    }

    const ProcessTrainingChartDataIntoFutureKinesisWorkoutCalendarEventItems = () => {

        const presentWeekOfTheYearKey = new WeekOfTheYearKey(trainingChartPresentWeekOfTheYearKey)

        var new_events = []

        for (let futureDataWeek of trainingChartLatestProgramSnapshot.programTable) {

            if (
                presentWeekOfTheYearKey.lessThan(new WeekOfTheYearKey(futureDataWeek.weekOfTheYearKey))
                && new WeekOfTheYearKey(futureDataWeek.weekOfTheYearKey).equals(new WeekOfTheYearKey(trainingChartActiveData.weekOfTheYearKey)) === false
            ) {

                const draggableDateRangeConstraint = GetDraggableDateRangeConstraintForEvent(futureDataWeek.weekOfTheYearKey);

                var weekOfTheYearKey = new WeekOfTheYearKey(futureDataWeek.weekOfTheYearKey)
                var weekOfTheYearKeyStartDate = weekOfTheYearKey.toDate()

                // Check if there's an override schedule for this week
                const weekOverride = overrideWeekDaySchedules?.find(
                    override => new WeekOfTheYearKey(override.weekOfTheYearKey).equals(weekOfTheYearKey)
                )

                // Use override schedule if available, otherwise use default
                const weekDayScheduleToUse = weekOverride?.weekDaySchedule || defaultWeekDaySchedule

                for (let weekDayIndex of Object.keys(weekDayScheduleToUse)) {

                    var workoutTemplateKey = weekDayScheduleToUse[weekDayIndex]
    
                    if (workoutTemplateKey !== 'rest') {
    
                        new_events.push({
                            calendarEventType: 'kinesis-workout',
                            title: GetWorkoutTemplateNameFromKey(workoutTemplateKey),
                            start: weekOfTheYearKeyStartDate.clone().add(weekDayIndex, 'days').format('YYYY-MM-DD'),
                            editable: canEditKinesisProfileWeekDaySchedules,
                            constraint: draggableDateRangeConstraint,
                            extendedProps: {
                                calendarDataKey: futureDataWeek.calendarDataKey,
                                weekOfTheYearKey: futureDataWeek.weekOfTheYearKey,
                                workoutTemplateKey: workoutTemplateKey,
                            }
                        })
    
                    }
    
                }

            }

        } 

        setFutureKinesisWorkoutCalendarEventItems(new_events)

    }

    const ProcessTrainingPeaksWorkoutsIntoCalendarItems = async () => {

        const calendarLowerBoundWeekOfTheYearKey = new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey)
        const dateLowerBound = calendarLowerBoundWeekOfTheYearKey.toDate().format('YYYY-MM-DD')

        const calendarUpperBoundWeekOfTheYearKey = new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).add(4)
        const dateUpperBound = calendarUpperBoundWeekOfTheYearKey.toDate().format('YYYY-MM-DD')

        const trainingPeaksWorkouts = await GetTrainingPeaksWorkouts(dateLowerBound, dateUpperBound)

        var new_training_peaks_workout_calendar_event_items = []

        for (let trainingPeaksWorkout of trainingPeaksWorkouts) {

            var new_item = {
                "calendarEventType": "training-peaks-workout",
                "title": trainingPeaksWorkout.Title,
                "start": trainingPeaksWorkout.WorkoutDay,
                "extendedProps": {
                    "calendarEventType": "training-peaks-workout",
                    "trainingPeaksWorkoutId": trainingPeaksWorkout.Id
                }
            }

            new_training_peaks_workout_calendar_event_items.push(new_item)
        }

        // console.log(trainingPeaksWorkouts)

        dispatch(addTrainingPeaksWorkoutCalendarEventItems(new_training_peaks_workout_calendar_event_items))
    }

    const ProcessDummyTrainingPeaksWorkoutIntoCalendarItems = () => {
        const dummy_item_1 = {
            "calendarEventType": "training-peaks-workout",
            "title": "Training Peaks Workout",
            "start": "2026-02-18",
            "extendedProps": {
                "calendarEventType": "training-peaks-workout",
                "trainingPeaksWorkoutId": 1234567
            }
        }

        const dummy_item_2 = {
            "calendarEventType": "training-peaks-workout",
            "title": "Workout Two",
            "start": "2026-02-19",
            "extendedProps": {
                "calendarEventType": "training-peaks-workout",
                "trainingPeaksWorkoutId": 123456734
            }
        }

        const dummy_item_3 = {
            "calendarEventType": "training-peaks-workout",
            "title": "WorkoutThreeReallyLongTitleWithNoSpacesSoThatTheTextWillOverflow",
            "start": "2026-02-21",
            "extendedProps": {
                "calendarEventType": "training-peaks-workout",
                "trainingPeaksWorkoutId": 123456734222
            }
        }

        dispatch(addTrainingPeaksWorkoutCalendarEventItems([dummy_item_1, dummy_item_2, dummy_item_3]))
    }

    const GetDraggableDateRangeConstraintForEvent = (weekOfTheYearKey) => {
        if (!weekOfTheYearKey) {
            return null;
        }

        const weekKey = new WeekOfTheYearKey(weekOfTheYearKey);
        const weekStart = weekKey.toDate().startOf('isoWeek');
        const weekEnd = weekKey.toDate().endOf('isoWeek').add(1, 'day');

        return {
            start: weekStart.format('YYYY-MM-DD'),
            end: weekEnd.format('YYYY-MM-DD')
        };
    }

    // Kinesis Workout Draggability Functions

    const ValidateKinesisWorkoutDragDrop = (dropInfo, draggedEvent) => {

        const targetDateString = dropInfo.startStr
        const workoutTemplateKey = draggedEvent.extendedProps.workoutTemplateKey
        const eventWeekOfTheYearKey = draggedEvent.extendedProps.weekOfTheYearKey
        
        if (!eventWeekOfTheYearKey) {
            return false;
        }

        // Determine if this is an active week or future week
        const currentActiveData = latestActiveDataRef.current
        const isActiveWeek = currentActiveData && new WeekOfTheYearKey(currentActiveData.weekOfTheYearKey).equals(new WeekOfTheYearKey(eventWeekOfTheYearKey))

        let processedWeekDaySchedule;

        if (isActiveWeek) {
            // Active week validation
            if (!currentActiveData || !currentActiveData.weekDaySchedule) {
                return false;
            }

            processedWeekDaySchedule = JSON.parse(JSON.stringify(currentActiveData.weekDaySchedule))
            if (currentActiveData.weekCompletionStatusKey === 'skipped') {
                processedWeekDaySchedule = RemoveStrengthWorkoutsFromWeekDaySchedule(processedWeekDaySchedule)
            }
        } else {
            // Future week validation - check for override or use default
            const weekOverride = overrideWeekDaySchedules?.find(
                override => new WeekOfTheYearKey(override.weekOfTheYearKey).equals(new WeekOfTheYearKey(eventWeekOfTheYearKey))
            )
            
            processedWeekDaySchedule = weekOverride?.weekDaySchedule || defaultWeekDaySchedule
            
            if (!processedWeekDaySchedule) {
                return false;
            }
        }

        // Init other data
        const targetDate = moment(targetDateString);
        const eventWeekKey = new WeekOfTheYearKey(eventWeekOfTheYearKey);
        const eventWeekStart = eventWeekKey.toDate().startOf('isoWeek');
        
        // Calculate day index (0-6) from week start (Monday = 0, Sunday = 6)
        const dayIndex = targetDate.diff(eventWeekStart, 'days').toString();
        
        // Check if this day has a workout (not 'rest')
        const daySchedule = processedWeekDaySchedule[dayIndex];
        
        // Return false if there's already a workout (not 'rest')
        if (
            daySchedule 
            && daySchedule !== 'rest'
            && daySchedule !== workoutTemplateKey // Allow item to be dragged to the same day it was already on
        ) {
            return false;
        }
        
        return true;
    }

    const RemoveStrengthWorkoutsFromWeekDaySchedule = (weekDaySchedule) => {
        for (let weekDayIndex of Object.keys(weekDaySchedule)) {
            if (
                weekDaySchedule[weekDayIndex] == 'strength-session-a' 
                || weekDaySchedule[weekDayIndex] == 'strength-session-b'
            ) {
                weekDaySchedule[weekDayIndex] = 'rest'
            }
        }

        return weekDaySchedule
    }

    const HandleActiveWeekKinesisWorkoutDragDrop = async (dropInfo) => {

        // Use ref to get latest state (avoids stale closure issues with rapid drags)
        const currentActiveData = latestActiveDataRef.current

        // Create duplicate of the active week weekDaySchedule
        var rollBackWeekDaySchedule = JSON.parse(JSON.stringify(currentActiveData.weekDaySchedule))
        var newWeekDaySchedule = JSON.parse(JSON.stringify(currentActiveData.weekDaySchedule))

        // console.log('Old')
        // console.log(newWeekDaySchedule)

        // Get the kinesis workout event data for workout being dragged
        const draggedEventExtendedProps = dropInfo.event._def.extendedProps;
        const draggedEventWorkoutTemplateKey = draggedEventExtendedProps.workoutTemplateKey;

        // Get the date the workout is being dropped to
        const dropDate = moment(dropInfo.event.start);

        // Calculate new day index (0-6) from week start (Monday = 0, Sunday = 6)
        const activeWeekOfTheYearKey = new WeekOfTheYearKey(currentActiveData.weekOfTheYearKey);
        const activeWeekStart = activeWeekOfTheYearKey.toDate().startOf('isoWeek');
        const dayIndex = dropDate.diff(activeWeekStart, 'days').toString();

        // console.log(newWeekDaySchedule)
        // console.log(draggedEventWorkoutTemplateKey)
        // console.log(dropDate.format('YYYY-MM-DD'))
        // console.log(dayIndex)

        // Remove the dragged event workout template key from the new week day schedule
        for (let key of Object.keys(newWeekDaySchedule)) {
            if (newWeekDaySchedule[key] === draggedEventWorkoutTemplateKey) {
                newWeekDaySchedule[key] = 'rest';
            }
        }

        // Set the dragged event workout template key to the new day index
        newWeekDaySchedule[dayIndex] = draggedEventWorkoutTemplateKey;

        // console.log('New')
        // console.log(newWeekDaySchedule)

        // Update redux store
        dispatch(setActiveWeekWeekDaySchedule(newWeekDaySchedule))

        // Track latest API call (discard any previous ones since only latest matters)
        const jsonBody = {
            groupId: groupId, // Send both to support group and individual profiles
            userId: athleteId, // Send both to support group and individual profiles
            presentWeekOfTheYearKey: currentActiveData.weekOfTheYearKey,
            weekDaySchedule: newWeekDaySchedule,
        }

        // console.log(jsonBody)

        weekDayScheduleSyncAPICounterRef.current += 1

        const currentCallId = weekDayScheduleSyncAPICounterRef.current

        latestWeekDayScheduleSyncDataRef.current = { jsonBody, rollBackWeekDaySchedule, callId: currentCallId }

        setSyncingMessage('Updating Kinesis Schedule...')

        ProcessLatestWeekDayScheduleSyncData()
    }

    const ProcessLatestWeekDayScheduleSyncData = async () => {
        // If already processing, it will check for newer calls when done
        if (isProcessingWeekDayScheduleSyncDataRef.current) {
            return
        }

        isProcessingWeekDayScheduleSyncDataRef.current = true

        while (latestWeekDayScheduleSyncDataRef.current !== null) {
            // Get the latest call (may have been updated while we were processing)
            const apiCall = latestWeekDayScheduleSyncDataRef.current

            const { jsonBody, rollBackWeekDaySchedule, callId } = apiCall

            // Clear the ref so we know if a newer call comes in
            latestWeekDayScheduleSyncDataRef.current = null

            try {
                // console.log(jsonBody)

                var result = null

                if (profileType === 'group') {
                    result = await WhichWeightAPI('PROGRAM', 'POST', '/present_group_week_day_schedule_json', jsonBody)
                }

                if (profileType === 'individual') {
                    result = await WhichWeightAPI('PROGRAM', 'POST', '/present_week_day_schedule_json', jsonBody)
                }

                // console.log(result)

                // Only process result if this is still the latest call
                if (callId === weekDayScheduleSyncAPICounterRef.current) {
                    if (result.data.status == 'failure') {
                        console.error('Failed to save present week day schedule to backend!! Rolling back to original week day schedule!!', result)
                        dispatch(setActiveWeekWeekDaySchedule(rollBackWeekDaySchedule))
                    }
                    setSyncingMessage('')
                }
                // If callId !== weekDayScheduleSyncAPICounterRef.current, a newer call came in, so we ignore this result
            } catch (error) {
                // Only rollback if this is still the latest call
                if (callId === weekDayScheduleSyncAPICounterRef.current) {
                    console.error('Error saving present week day schedule to backend!! Rolling back to original week day schedule!!', error)
                    dispatch(setActiveWeekWeekDaySchedule(rollBackWeekDaySchedule))
                    setSyncingMessage('')
                }
            }

            // Check if a newer call came in while we were processing
            if (latestWeekDayScheduleSyncDataRef.current !== null) {
                // A newer call is waiting, process it
                continue
            } else {
                // No more calls, we're done
                break
            }
        }

        isProcessingWeekDayScheduleSyncDataRef.current = false
    }

    const HandleFutureWeekKinesisWorkoutDragDrop = async (dropInfo) => {

        // Get the kinesis workout event data for workout being dragged
        const draggedEventExtendedProps = dropInfo.event._def.extendedProps;
        const draggedEventWorkoutTemplateKey = draggedEventExtendedProps.workoutTemplateKey;
        const draggedEventWeekOfTheYearKey = draggedEventExtendedProps.weekOfTheYearKey;

        if (!draggedEventWeekOfTheYearKey || !draggedEventWorkoutTemplateKey) {
            return;
        }

        // Get the date the workout is being dropped to
        const dropDate = moment(dropInfo.event.start);

        // Calculate new day index (0-6) from week start (Monday = 0, Sunday = 6)
        const eventWeekKey = new WeekOfTheYearKey(draggedEventWeekOfTheYearKey);
        const eventWeekStart = eventWeekKey.toDate().startOf('isoWeek');
        const dayIndex = dropDate.diff(eventWeekStart, 'days').toString();

        // Get current schedule (override or default)
        const weekOverride = overrideWeekDaySchedules?.find(
            override => new WeekOfTheYearKey(override.weekOfTheYearKey).equals(eventWeekKey)
        )
        
        const currentWeekDaySchedule = weekOverride?.weekDaySchedule || defaultWeekDaySchedule
        const rollBackWeekDaySchedule = JSON.parse(JSON.stringify(currentWeekDaySchedule))
        const newWeekDaySchedule = JSON.parse(JSON.stringify(currentWeekDaySchedule))

        // Remove the dragged event workout template key from the new week day schedule
        for (let key of Object.keys(newWeekDaySchedule)) {
            if (newWeekDaySchedule[key] === draggedEventWorkoutTemplateKey) {
                newWeekDaySchedule[key] = 'rest';
            }
        }

        // Set the dragged event workout template key to the new day index
        newWeekDaySchedule[dayIndex] = draggedEventWorkoutTemplateKey;

        // Update redux store immediately
        dispatch(addOverrideWeekDaySchedule({
            weekOfTheYearKey: draggedEventWeekOfTheYearKey,
            weekDaySchedule: newWeekDaySchedule
        }))

        // Track latest API call (discard any previous ones since only latest matters)
        const jsonBody = {
            groupId: groupId, // Send both to support group and individual profiles
            userId: athleteId, // Send both to support group and individual profiles
            weekOfTheYearKey: draggedEventWeekOfTheYearKey,
            weekDaySchedule: newWeekDaySchedule,
        }

        overrideWeekDayScheduleSyncAPICounterRef.current += 1

        const currentCallId = overrideWeekDayScheduleSyncAPICounterRef.current

        latestOverrideWeekDayScheduleSyncDataRef.current = { 
            jsonBody, 
            rollBackWeekOfTheYearKey: draggedEventWeekOfTheYearKey,
            rollBackWeekDaySchedule,
            callId: currentCallId 
        }

        setSyncingMessage('Updating Kinesis Schedule...')

        ProcessLatestOverrideWeekDayScheduleSyncData()
    }

    const ProcessLatestOverrideWeekDayScheduleSyncData = async () => {
        // If already processing, it will check for newer calls when done
        if (isProcessingOverrideWeekDayScheduleSyncDataRef.current) {
            return
        }

        isProcessingOverrideWeekDayScheduleSyncDataRef.current = true

        while (latestOverrideWeekDayScheduleSyncDataRef.current !== null) {
            // Get the latest call (may have been updated while we were processing)
            const apiCall = latestOverrideWeekDayScheduleSyncDataRef.current

            const { jsonBody, rollBackWeekOfTheYearKey, rollBackWeekDaySchedule, callId } = apiCall

            // Clear the ref so we know if a newer call comes in
            latestOverrideWeekDayScheduleSyncDataRef.current = null

            try {
                // console.log(jsonBody)

                var result = null

                if (profileType === 'group') {
                    result = await WhichWeightAPI('PROGRAM', 'POST', '/override_group_week_day_schedule_json', jsonBody)
                }

                if (profileType === 'individual') {
                    result = await WhichWeightAPI('PROGRAM', 'POST', '/override_week_day_schedule_json', jsonBody)
                }

                // console.log(result)

                // Only process result if this is still the latest call
                if (callId === overrideWeekDayScheduleSyncAPICounterRef.current) {
                    if (result.data.status == 'failure') {
                        console.error('Failed to save override week day schedule to backend!! Rolling back!!', result)
                        // Remove the override from Redux to rollback
                        dispatch(removeOverrideWeekDaySchedule({ weekOfTheYearKey: rollBackWeekOfTheYearKey }))
                    }
                    setSyncingMessage('')
                }
                // If callId !== overrideWeekDayScheduleSyncAPICounterRef.current, a newer call came in, so we ignore this result
            } catch (error) {
                // Only rollback if this is still the latest call
                if (callId === overrideWeekDayScheduleSyncAPICounterRef.current) {
                    console.error('Error saving override week day schedule to backend!! Rolling back!!', error)
                    // Remove the override from Redux to rollback
                    dispatch(removeOverrideWeekDaySchedule({ weekOfTheYearKey: rollBackWeekOfTheYearKey }))
                    setSyncingMessage('')
                }
            }

            // Check if a newer call came in while we were processing
            if (latestOverrideWeekDayScheduleSyncDataRef.current !== null) {
                // A newer call is waiting, process it
                continue
            } else {
                // No more calls, we're done
                break
            }
        }

        isProcessingOverrideWeekDayScheduleSyncDataRef.current = false
    }

    const HandleKinesisWorkoutDragDrop = async (dropInfo) => {

        // Unified handler that routes to active or future week handler

        const draggedEventExtendedProps = dropInfo.event._def.extendedProps;
        const draggedEventWeekOfTheYearKey = draggedEventExtendedProps.weekOfTheYearKey;
        
        if (!draggedEventWeekOfTheYearKey) {
            return;
        }

        // Determine if this is an active week or future week
        const currentActiveData = latestActiveDataRef.current
        const isActiveWeek = currentActiveData && new WeekOfTheYearKey(currentActiveData.weekOfTheYearKey).equals(new WeekOfTheYearKey(draggedEventWeekOfTheYearKey))

        if (isActiveWeek) {
            // Route to active week handler
            HandleActiveWeekKinesisWorkoutDragDrop(dropInfo)
        } else {
            // Route to future week handler
            HandleFutureWeekKinesisWorkoutDragDrop(dropInfo)
        }
    }

    // Override functionality

    const ClearOverrides = () => {
        dispatch(clearProgramTableOverrides())
    }

    const SaveOverrides = async () => {
        if (!cachedOverrides || cachedOverrides.length === 0){
            return
        }

        setSavingOverrides(true)

        try {
            if (profileType === 'group') {
                const jsonBody = {
                    groupId: groupId,
                    overrides: JSON.stringify(cachedOverrides)
                }

                // console.log(jsonBody)

                const result = await WhichWeightAPI('PROGRAM', 'POST', '/one_off_coach_group_override_post', jsonBody)

                // console.log(result)

                if (result.data.status === 'failure') {
                    console.error(result)
                    return
                }
            }

            if (profileType === 'individual') {
                const jsonBody = {
                    userId: athleteId,
                    overrides: JSON.stringify(cachedOverrides)
                }

                // console.log(jsonBody)

                const result = await WhichWeightAPI('PROGRAM', 'POST', '/one_off_coach_override_post', jsonBody)

                // console.log(result)

                if (result.data.status === 'failure') {
                    console.error(result)
                    return
                }
            }

            // console.log(result)
            
            window.location.reload()

        } catch (error) {
            console.error('Error saving overrides:', error)
        } finally {
            setSavingOverrides(false)
        }
    }

    // Entire calendar events array
    const calendarEvents = useMemo(() => {
        return [
            ...kinesisEventCalendarItems, 
            ...pastKinesisWorkoutCalendarEventItems, 
            ...activeKinesisWorkoutCalendarEventItems, 
            ...futureKinesisWorkoutCalendarEventItems,
            ...trainingPeaksWorkoutCalendarEventItems
        ]
    }, 
        [
            kinesisEventCalendarItems, 
            pastKinesisWorkoutCalendarEventItems, 
            activeKinesisWorkoutCalendarEventItems, 
            futureKinesisWorkoutCalendarEventItems,
            trainingPeaksWorkoutCalendarEventItems
        ]
    )

    // Keep ref in sync with latest Redux state to avoid stale closures
    useEffect(() => {
        latestActiveDataRef.current = trainingChartActiveData
    }, [trainingChartActiveData])

    // Process Kinesis events into calendar items
    useEffect(() => {
        if (kinesisEvents !== null) {
            ProcessKinesisEventsIntoCalendarItems()
        }
    }, [kinesisEvents])

    // Process training chart past data into calendar items
    useEffect(() => { 
        if (trainingChartPastData.length > 0) {
            ProcessTrainingChartDataIntoPastKinesisWorkoutCalendarEventItems()
        }
    }, [trainingChartPastData])

    // Process training chart active data into calendar items
    useEffect(() => { 
        if (trainingChartActiveData !== null) {
            ProcessTrainingChartDataIntoActiveKinesisWorkoutCalendarEventItems()
        }
    }, [trainingChartActiveData])

    // Process training chart future data into calendar items
    useEffect(() => { 
        if (trainingChartLatestProgramSnapshot !== null) {
            ProcessTrainingChartDataIntoFutureKinesisWorkoutCalendarEventItems()
        }
    }, [trainingChartLatestProgramSnapshot, overrideWeekDaySchedules, defaultWeekDaySchedule])

    // Handle keyboard up/down navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            // If input or textarea is focused, do not navigate
            if (
                document.activeElement.tagName === 'INPUT' || 
                document.activeElement.tagName === 'TEXTAREA'
                || modalData !== null
            ) {
                return;
            }

            if (e.key === 'ArrowUp') {
                NavigateCalendarToPreviousWeek();
                e.preventDefault();
            } else if (e.key === 'ArrowDown') {
                NavigateCalendarToNextWeek();
                e.preventDefault();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [trainingChartSelectedWeekOfTheYearKey, modalData, NavigateCalendarToPreviousWeek, NavigateCalendarToNextWeek])

    // Load in TrainingPeaks workouts when calendar week changes
    useEffect(() => {

        // Dummy item for testing
        // ProcessDummyTrainingPeaksWorkoutIntoCalendarItems()
        
        if (trainingPeaksUserId === null) return
        if (trainingPeaksUserId === undefined) return
        if (trainingPeaksUserId === '') return
        if (trainingPeaksUserId.length === 0) return

        // Debounce: wait 1 second before calling, reset timer if week changes again
        const timeoutId = setTimeout(() => {
            ProcessTrainingPeaksWorkoutsIntoCalendarItems()
        }, 1000)

        // Cleanup: clear timeout if dependency changes or component unmounts
        return () => {
            clearTimeout(timeoutId)
        }

    }, [trainingChartSelectedWeekOfTheYearKey])

    return (
        <ContentFrame className='flex-grow'>
            <div className='h-full w-full flex relative'>

                {/* Navigation Buttons */}
                <div className='h-full w-8 flex flex-col rounded-l-2xl divide-y divide-gray-200'>
                    <button
                        className='flex-grow flex items-center justify-center cursor-pointer text-gray-800 focus:outline-none'
                        aria-label="Previous week"
                        onClick={NavigateCalendarToPreviousWeek}
                    >
                        <ChevronUpIcon className='h-5 w-5' />
                    </button>

                    <button
                        className='flex-grow flex items-center justify-center cursor-pointer text-gray-800 focus:outline-none'
                        aria-label="Next week"
                        onClick={NavigateCalendarToNextWeek}
                    >
                        <ChevronDownIcon className='h-5 w-5' />
                    </button>
                </div>

                {/* Calendar Component with Custom Sidebar */}
                <div 
                    ref={calendarContainerRef} 
                    className='-my-[2px] flex flex-col flex-grow overflow-hidden relative pr-8'
                >
                    <FullCalendar
                        longPressDelay={250}
                        ref={calendarRef}
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        views={CalendarViewsConfig}
                        now={trainingChartPresentDate}
                        initialView="fourWeek"
                        initialDate={new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).toDate().format('YYYY-MM-DD')} // Start from selected week
                        firstDay={1} // ISO week start (Monday)
                        height="100%"
                        headerToolbar={false}
                        dayHeaders={true}
                        dayHeaderContent={GetDayHeaderContent}
                        events={calendarEvents}
                        dayCellContent={GetDayCellContent}
                        dayCellClassNames={GetDayCellClassNames}
                        eventContent={(arg) => <CalendarEventItemRouter eventData={arg.event} />}
                        eventAllow={ValidateKinesisWorkoutDragDrop}
                        eventDrop={HandleKinesisWorkoutDragDrop}
                    />
                    
                    <PresentWeekHighlighter 
                        calendarRef={calendarRef}
                        calendarContainerRef={calendarContainerRef}
                        calendarEvents={calendarEvents}
                    />

                    <AthleteCalendarProgramWeeks 
                        calendarRef={calendarRef}
                        calendarEvents={calendarEvents}
                    />
                </div>

                {/* Syncing Indicator */}
                <div className='absolute bottom-0 right-0 p-3 z-10 flex items-center gap-x-2'>
                    {syncingMessage !== '' && (
                        <div className='flex items-center gap-x-2 bg-white border border-gray-400 rounded-2xl py-2 px-6 shadow-lg'>
                            <img src={loadingSpinner} alt="Loading" className="spinner w-6 h-6" />
                            <p className='w-full text-sm text-gray-500'>{syncingMessage}</p>
                        </div>
                    )}

                    {cachedOverrides.length > 0 && (
                        <div className='flex items-center gap-x-2'>
                            <div className='w-52'>
                                <OutlineButton
                                    text={`Clear Overrides (${cachedOverrides.length})`}
                                    action={ClearOverrides}
                                    className="hover:bg-gray-50 shadow-lg"
                                />
                            </div>
                        
                            <div className='w-52'>
                                <BrandColorButton
                                    text={`Save Overrides (${cachedOverrides.length})`}
                                    action={SaveOverrides}
                                    isLoading={savingOverrides}
                                    className="shadow-lg"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </ContentFrame>
    )
}